import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./TCList.scss";
import apiClient from "../../../../../../Config/apiClient";
import { DATE_FORMAT, DATE_MONTH_FORMAT } from "../../../Utils/Constants";
import moment from "moment";

import filterIcon from "../../../../../../Assets/images/filter-icon.svg";
import clearIcon from "../../../../../../Assets/images/clear-icon.svg";
import searchIcon from "../../../../../../Assets/images/search-icon.svg";
import closeRedIcon from "../../../../../../Assets/images/close-red.svg";
import calendarIcon from "../../../../../../Assets/images/calender.svg";
import ReasonModal from "../../DisableModal/DisableModal";
import ConfirmationModal from "../../../../../../Components/ConfirmationBox/ConfirmationModal";
import { showSuccessToast, showErrorToast } from "../../../../../../Context/Toaster/Toaster";
import TCInfoModal from "../ViewModal/TCInfoModal";
import { hasPermission } from "../../../Services/permission";

const TruckCompanyUrl = process.env.REACT_APP_HS_TRUCK_COMPANY_BASE_PATH;

const TC_SECTION = "Truck Companies";
const TC_UPDATE_KEY = ["tc_manage"];

const TruckCompanyList = ({
  companiesPerPage,
  currentPage,
  onPageChange,
  setCompaniesPerPage,
  searchTerm,
  onSearchChange,
}) => {
  const [companies, setCompanies] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [disableCompanyId, setDisableCompanyId] = useState(null);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [showCompanyProfileModal, setShowCompanyProfileModal] = useState(false);

  const totalCompanies = companies.length;
  const totalPages = Math.ceil(totalCompanies / companiesPerPage);

  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterRef = useRef(null);
  const [localFilters, setLocalFilters] = useState({
    created_date_from: "",
    created_date_to: "",
    status: "",
  });

  const [filterErrors, setFilterErrors] = useState({
    created_date: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    created_date_from: "",
    created_date_to: "",
    status: "",
  });

  // Fetch companies from API
  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`${TruckCompanyUrl}/`);
      if (res.data.status === "SUCCESS") {
        const normalized = res.data.data.map((c) => ({
          ...c,
          status: c.is_disabled ? "Disabled"
            : c.is_activated ? "Approved"
              : "Un approved",
        }));
        setCompanies(normalized);
      } else {
        setCompanies([]);
      }
    } catch (error) {
      console.error("Error fetching truck companies:", error);
      setCompanies([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleOpenDisableModal = (companyId) => {
    setDisableCompanyId(companyId);
    setShowReasonModal(true);
  };

  // Pagination
  const startIndex = (currentPage - 1) * companiesPerPage;
  const currentCompanies = companies.slice(
    startIndex,
    startIndex + companiesPerPage
  );

  // Click outside filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLocalFilterChange = (key, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setFilterErrors({ created_date: "" });
  };

  const clearIndividualFilter = (field1, field2 = null) => {
    const updated = { ...localFilters, [field1]: "" };
    if (field2) updated[field2] = "";
    setLocalFilters(updated);
  };

  const handleClearFilterForm = () => {
    setLocalFilters({
      created_date_from: "",
      created_date_to: "",
      status: "",
    });
    setFilterErrors({ created_date: "" });
  };

  const validateFilterDates = () => {
    const errors = { created_date: "" };
    let isValid = true;

    if (
      localFilters.created_date_from &&
      localFilters.created_date_to &&
      new Date(localFilters.created_date_from) >
      new Date(localFilters.created_date_to)
    ) {
      errors.created_date = "End Date must be after Start Date.";
      isValid = false;
    }

    setFilterErrors(errors);
    return isValid;
  };

  const handleApplyFilter = () => {
    if (!validateFilterDates()) return;
    setAppliedFilters(localFilters);
    setShowFilterDropdown(false);
  };

  const handleClearAllFilters = () => {
    handleClearFilterForm();
    setAppliedFilters({ created_date_from: "", created_date_to: "", status: "" });
    setShowFilterDropdown(false);
  };

  const isAnyFilterApplied = Object.values(appliedFilters).some((val) => Boolean(val));

  const filteredCompanies = companies.filter((c) => {
    return (
      (!searchTerm ||
        (c.company_name &&
          c.company_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.corporate_name &&
          c.corporate_name.toLowerCase().includes(searchTerm.toLowerCase()))) &&
      (!appliedFilters.status || c.status === appliedFilters.status) &&
      (!appliedFilters.created_date_from ||
        new Date(c.created_date_time) >= new Date(appliedFilters.created_date_from)) &&
      (!appliedFilters.created_date_to ||
        new Date(c.created_date_time) <= new Date(appliedFilters.created_date_to))
    );
  });

  // Approve company
  const handleActivateCompany = async (id) => {
    try {
      setLoading(true);
      const res = await apiClient.put(`${TruckCompanyUrl}/${id}/activate`);
      if (res.data.status === "SUCCESS") {
        setCompanies((prev) =>
          prev.map((c) =>
            c.id === id
              ? { ...c, is_activated: true, is_disabled: false, status: "Approved" }
              : c
          )
        );
        setShowConfirm(false);
        showSuccessToast("Company approved successfully!");
      } else {
        showErrorToast(res.data.message || "Failed to approve company!");
      }
    } catch (error) {
      console.error("Error approving company:", error);
      showErrorToast("Failed to approve company!");
    } finally {
      setLoading(false);
    }
  };

  // Disable company
  const handleDisableCompany = async (reason) => {
    if (!disableCompanyId) return;
    try {
      setLoading(true);
      const res = await apiClient.put(`${TruckCompanyUrl}/${disableCompanyId}/disable`, { reason });
      if (res.data.status === "SUCCESS") {
        setCompanies((prev) =>
          prev.map((c) =>
            c.id === disableCompanyId
              ? { ...c, is_activated: false, is_disabled: true, status: "Disabled" }
              : c
          )
        );
        showSuccessToast("Company disabled successfully!");
      } else {
        showErrorToast(res.data.message || "Failed to disable company!");
      }
    } catch (error) {
      console.error("Error disabling company:", error);
      showErrorToast("Failed to disable company!");
    } finally {
      setLoading(false);
      setShowReasonModal(false);
      setDisableCompanyId(null);
    }
  };

  // Activate after disabled
  const handleActivateDisabledCompany = async (id) => {
    try {
      setLoading(true);
      const res = await apiClient.put(`${TruckCompanyUrl}/${id}/activate`);
      if (res.data.status === "SUCCESS") {
        setCompanies((prev) =>
          prev.map((c) =>
            c.id === id
              ? { ...c, is_activated: true, is_disabled: false, status: "Approved" }
              : c
          )
        );
        showSuccessToast("Company activated successfully!");
      } else {
        showErrorToast(res.data.message || "Failed to activate company!");
      }
    } catch (error) {
      console.error("Error activating company:", error);
      showErrorToast("Failed to activate company!");
    } finally {
      setLoading(false);
    }
  };

  const canEdit = hasPermission({ section: TC_SECTION, keys: TC_UPDATE_KEY });

  return (
    <>
      {/* Top Filters */}
      <div className="common-top exm-top">
        <div className="filter-cvr" ref={filterRef}>
          <a
            className={`filter-btn ${isAnyFilterApplied ? "active-filter" : ""}`}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowFilterDropdown((prev) => !prev);
            }}
          >
            <span className="filter-icon-wrapper">
              <img className="ct-icon" src={filterIcon} alt="filter" />
              {isAnyFilterApplied && <span className="filter-indicator-dot"></span>}
            </span>
            Filter
          </a>

          <a
            className="clear-btn"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleClearAllFilters();
            }}
          >
            <img className="ct-icon" src={clearIcon} alt="clear" />
            Clear Filter
          </a>

          {showFilterDropdown && (
            <ul className="dropdown-menu filterdrop-body show dropstyle">
              <div className="filterdrop-split">
                <label className="filter-label">
                  Status
                  {localFilters.status && (
                    <img
                      className="red-close"
                      src={closeRedIcon}
                      alt="remove"
                      onClick={() => clearIndividualFilter("status")}
                      style={{ cursor: "pointer" }}
                    />
                  )}
                </label>
                <select
                  className="filter-select"
                  value={localFilters.status}
                  onChange={(e) => handleLocalFilterChange("status", e.target.value)}
                >
                  <option value="">--select--</option>
                  <option value="Un approved">Un Approved</option>
                  <option value="Approved">Approved</option>
                  <option value="Disabled">Disabled</option>
                </select>
              </div>

              <div className="filterdrop-split">
                <label className="filter-label">
                  Created Date
                  {(localFilters.created_date_from || localFilters.created_date_to) && (
                    <img
                      className="red-close"
                      src={closeRedIcon}
                      alt="remove"
                      onClick={() =>
                        clearIndividualFilter("created_date_from", "created_date_to")
                      }
                      style={{ cursor: "pointer" }}
                    />
                  )}
                </label>
                <div className="filter-datepicker from-date">
                  <DatePicker
                    selected={
                      localFilters.created_date_from
                        ? new Date(localFilters.created_date_from)
                        : null
                    }
                    onChange={(date) =>
                      handleLocalFilterChange(
                        "created_date_from",
                        date ? date.toLocaleDateString("en-CA") : ""
                      )
                    }
                    placeholderText="Start Date"
                    className="filter-date"
                    dateFormat={DATE_MONTH_FORMAT}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    popperProps={{ strategy: 'fixed' }}
                  />
                  <span className="filter-calendar-icon">
                    <img src={calendarIcon} alt="calendar" />
                  </span>
                </div>
                <div className="filter-datepicker to-date">
                  <DatePicker
                    selected={
                      localFilters.created_date_to
                        ? new Date(localFilters.created_date_to)
                        : null
                    }
                    onChange={(date) =>
                      handleLocalFilterChange(
                        "created_date_to",
                        date ? date.toLocaleDateString("en-CA") : ""
                      )
                    }
                    placeholderText="End Date"
                    className="filter-date"
                    dateFormat={DATE_MONTH_FORMAT}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    popperProps={{ strategy: 'fixed' }}
                  />
                  <span className="filter-calendar-icon">
                    <img src={calendarIcon} alt="calendar" />
                  </span>
                </div>
                {filterErrors.created_date && (
                  <div className="error-text active">{filterErrors.created_date}</div>
                )}
              </div>

              <div className="filterdrop-split mb-0">
                <div className="filter-buttons">
                  <a
                    href="#"
                    className="filter-clear"
                    onClick={(e) => {
                      e.preventDefault();
                      handleClearFilterForm();
                    }}
                  >
                    Clear
                  </a>
                  <a
                    href="#"
                    className="filter-apply"
                    onClick={(e) => {
                      e.preventDefault();
                      handleApplyFilter();
                    }}
                  >
                    Apply
                  </a>
                </div>
              </div>
            </ul>
          )}
        </div>

        <div className="search-bar">
          <form onSubmit={(e) => e.preventDefault()}>
            <input
              className="form-control me-2"
              type="search"
              placeholder="Search Company"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            <button className="ser-submit" type="submit">
              <img src={searchIcon} alt="search" />
            </button>
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="table-cvr">
        <div className="table-inner">
          <table className="table nowrap">
            <thead>
              <tr>
                <th>#</th>
                <th>Company Name</th>
                <th>Corporation Name</th>
                <th>Address</th>
                <th>Phone No</th>
                <th>Email</th>
                <th>Created On</th>
                <th>Actions</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    <ClipLoader size={30} color="gray" />
                  </td>
                </tr>
              ) : filteredCompanies.length > 0 ? (
                filteredCompanies.map((c, index) => (
                  <tr key={c.id}>
                    <td>{startIndex + index + 1}</td>
                    <td><a
                      className="vu-link"
                      href="#"
                      style={{
                        color: "#59a52c",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={(e) => { e.preventDefault(); setSelectedCompanyId(c.id); setShowCompanyProfileModal(true); }}
                    >
                      {c.company_name}
                    </a></td>
                    <td>{c.corporate_name}</td>
                    <td>{c.address1}</td>
                    <td>{c.contact_no}</td>
                    <td>{c.email_id}</td>
                    <td>
                      {c.created_date_time
                        ? moment(c.created_date_time).format(DATE_FORMAT)
                        : "-"}
                    </td>
                    <td>
                      <button
                        className="status-btn"
                        style={{
                          backgroundColor: "#2a5caa",
                          color: "white",
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "13px",
                          minWidth: "100px"
                        }}
                        onClick={() => {
                          navigate(`/hs/t-companies/${c.id}/trucks?name=${encodeURIComponent(c.company_name)}`);
                        }}
                      >
                        View Trucks
                      </button>
                    </td>
                    <td>
                      {canEdit && ( 
                        c.is_disabled || c.status === "Disabled" ? (
                          <button
                            className="status-btn activate"
                            onClick={() => {
                              setSelectedCompanyId(c.id);
                              setShowActivateConfirm(true);
                            }}
                          >
                            Activate
                          </button>
                        ) : !c.is_activated ? (
                          <button
                            className="status-btn approve"
                            onClick={() => {
                              setSelectedCompanyId(c.id);
                              setShowConfirm(true);
                            }}
                          >
                            Approve
                          </button>
                        ) : (
                          <button
                            className="status-btn disable"
                            onClick={() => handleOpenDisableModal(c.id)}
                          >
                            Disable
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">
                    No Truck Company found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="dt-layout-row d-flex align-items-center justify-content-between">
          <div className="dt-length">
            Showing{" "}
            <select
              className="dt-input"
              value={companiesPerPage}
              onChange={(e) => {
                onPageChange(1);
                setCompaniesPerPage(Number(e.target.value));
              }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>{" "}
            of {totalCompanies} entries
          </div>

          <div className="dt-paging d-flex align-items-center" style={{ gap: "5px" }}>
            <button
              className="dt-paging-button previous"
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              Previous
            </button>

            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i + 1}
                className={`dt-paging-button ${currentPage === i + 1 ? "current" : ""}`}
                onClick={() => onPageChange(i + 1)}
              >
                {i + 1}
              </button>
            ))}

            <button
              className="dt-paging-button next"
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>

        {/* Confirmation Modal */}
        <ConfirmationModal
          id="approveConfirmModal"
          show={showConfirm}
          title="Approve Company"
          message="Are you sure you want to approve this company?"
          confirmText="Approve"
          cancelText="Cancel"
          onConfirm={() => handleActivateCompany(selectedCompanyId)}
          onCancel={() => setShowConfirm(false)}
        />

        <ConfirmationModal
          id="activateConfirmModal"
          show={showActivateConfirm}
          title="Activate Company"
          message="Are you sure you want to activate this company?"
          confirmText="Activate"
          cancelText="Cancel"
          onConfirm={async () => {
            await handleActivateDisabledCompany(selectedCompanyId);
            setShowActivateConfirm(false);
          }}
          onCancel={() => setShowActivateConfirm(false)}
        />

        <ReasonModal
          id="disableReasonModal"
          show={showReasonModal}
          title="Disable Company"
          onCancel={() => setShowReasonModal(false)}
          onDisable={handleDisableCompany}
        />

        {showCompanyProfileModal && (
          <TCInfoModal canEdit={canEdit}
          onClose={() => setShowCompanyProfileModal(false)}
          onUpdated={() => fetchCompanies()}
          companyId={selectedCompanyId} />
        )}
      </div>
    </>
  );
};

export default TruckCompanyList;
